import { FC, useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Typography,
  MenuItem,
  Select,
  FormControl,
  SelectChangeEvent,
  useTheme,
} from "@mui/material"; // Use SelectChangeEvent from MUI
import { useTranslation } from "react-i18next"; // Import useTranslation

import { ROUTES } from "constants/routes";
import ChevronDown from "icons/ChevronDown";
import Logo from "icons/Logo";
import Activity from "icons/Activity";
import ActivityFilled from "icons/ActivityFilled";
import Exit from "icons/Exit";

import { Avatar } from "../Avatar";
import { Tooltip } from "../Tooltip";

import {
  StyledCollapseBtn,
  StyledSidebar,
  StyledSidebarContainer,
  StyledSidebarFooter,
  StyledSidebarHeader,
  StyledSidebarLogOutBtn,
  StyledSidebarLogo,
  StyledSidebarMenu,
  StyledSidebarMenuItem,
  StyledSidebarUser,
  StyledSidebarUserInfo,
  StyledSidebarVersionContainer,
  StyledUnCollapseBtn,
} from "styles/components/sidebar";

type SidebarNavItem = {
  title: string;
  route?: string;
  icon?: JSX.Element;
  activeIcon?: JSX.Element;
  disabled?: boolean;
};

type User = {
  avatar?: string;
  name: string;
};

interface SidebarProps {
  nav?: SidebarNavItem[];
  user?: User;
  activeNavBtn?: string;
  availability?: boolean;
  version?: string;

  onUserClick?: () => void;
  setActiveNavBtn?: (title: string) => void;
  setAvailability?: (availability: boolean) => void;
  onLogOut?: () => void;
}

const initialSidebarNav: SidebarNavItem[] = [
  {
    title: "my_experiments",
    route: ROUTES.home,
    icon: <Activity />,
    activeIcon: <ActivityFilled />,
  },
];

const Sidebar: FC<SidebarProps> = ({
  nav = initialSidebarNav,
  activeNavBtn = nav[0].title,
  availability,
  user,
  version,
  onUserClick,
  setActiveNavBtn,
  setAvailability,
  onLogOut,
}) => {
  const [isCollapsed, setIsCollapsed] = useState<boolean>(false);
  const [tooltipIsVisible, setTooltipIsVisible] = useState<boolean>(false);

  const { t, i18n } = useTranslation(); // Initialize translation

  const theme = useTheme();

  const navigate = useNavigate();

  const handleSidebarCollapse = useCallback(
    () => setIsCollapsed((prev) => !prev),
    []
  );

  const handleNavBtnClick = useCallback(
    (title: string, route?: string) => () => {
      if (route) {
        navigate(route);
      }

      setActiveNavBtn?.(title);
    },
    [navigate, setActiveNavBtn]
  );

  const handleLanguageChange = useCallback(
    (event: SelectChangeEvent<string>) => {
      // Use the correct type
      const newLanguage = event.target.value as string;
      i18n.changeLanguage(newLanguage);
      localStorage.setItem("i18nextLng", newLanguage);
      //document.documentElement.dir = newLanguage === 'en' ? 'ltr' : 'rtl';
    },
    [i18n]
  );

  const renderedFooter = useMemo(
    () =>
      user && user.name ? (
        <StyledSidebarFooter>
          <StyledSidebarUser collapsed={+isCollapsed} onClick={onUserClick}>
            <Avatar src={user.avatar} name={user.name} alt={user.name} />
            <StyledSidebarUserInfo collapsed={+isCollapsed}>
              <Typography variant="bodyS" fontWeight={300}>
                {t("taster")}
              </Typography>
              <Typography
                variant="bodyM"
                fontWeight={400}
                textOverflow="ellipsis"
                width="100%"
                display="block"
                whiteSpace="nowrap"
                overflow="hidden"
              >
                {user.name}
              </Typography>
            </StyledSidebarUserInfo>
          </StyledSidebarUser>
          <StyledSidebarLogOutBtn collapsed={+isCollapsed} onClick={onLogOut}>
            <span className="log-out-btn__icon">
              <Exit />
            </span>
            <span className="log-out-btn__text">{t("log_out")}</span>
          </StyledSidebarLogOutBtn>
          {/* Language Selection Dropdown */}
          <FormControl
            variant="outlined"
            fullWidth
            style={{ marginTop: "1rem" }}
          >
            <Select
              value={i18n.language}
              onChange={handleLanguageChange}
              displayEmpty
              inputProps={{ "aria-label": "Without label" }}
            >
              <MenuItem value="en">English</MenuItem>
              <MenuItem value="he">עברית</MenuItem>
              <MenuItem value="ar">العربية</MenuItem>
              {/* Add more languages here */}
            </Select>
          </FormControl>
        </StyledSidebarFooter>
      ) : null,
    [
      user,
      isCollapsed,
      onUserClick,
      t,
      onLogOut,
      i18n.language,
      handleLanguageChange,
    ]
  );

  const renderedVersionTooltipText = useMemo(
    () =>
      version &&
      ((isCollapsed && version.length > 3) ||
        (!isCollapsed && version.length > 30))
        ? version
        : undefined,
    [isCollapsed, version]
  );

  const renderedVersion = useMemo(
    () =>
      version ? (
        <StyledSidebarVersionContainer>
          <Tooltip
            text={renderedVersionTooltipText}
            position="right"
            size="auto"
          >
            <Typography
              variant="bodyM"
              fontWeight={400}
              textOverflow="ellipsis"
              width="100%"
              display="block"
              whiteSpace="nowrap"
              overflow="hidden"
              color={theme.palette.customGrey.main}
            >
              {version}
            </Typography>
          </Tooltip>
        </StyledSidebarVersionContainer>
      ) : null,
    [renderedVersionTooltipText, theme.palette.customGrey.main, version]
  );

  return (
    <StyledSidebar className="sidebar" collapsed={isCollapsed}>
      <StyledSidebarContainer>
        <StyledSidebarHeader>
          <StyledSidebarLogo collapsed={+isCollapsed} to={ROUTES.home}>
            <Logo />
          </StyledSidebarLogo>
          <StyledCollapseBtn
            onClick={handleSidebarCollapse}
            collapsed={+isCollapsed}
          >
            <ChevronDown />
          </StyledCollapseBtn>
          <StyledUnCollapseBtn
            collapsed={+isCollapsed}
            onClick={handleSidebarCollapse}
          >
            <ChevronDown />
          </StyledUnCollapseBtn>
        </StyledSidebarHeader>
        <StyledSidebarMenu tooltipIsVisible={+tooltipIsVisible}>
          {nav?.map(({ title, route, icon, activeIcon, disabled }) => (
            <Tooltip
              key={title}
              text={t(title)} // Translate title
              position="right"
              size="auto"
              getVisible={(value: boolean) => setTooltipIsVisible(value)}
              disabled={!isCollapsed}
            >
              <StyledSidebarMenuItem
                component={
                  <button
                    className="sidebar__btn"
                    onClick={handleNavBtnClick(title, route)}
                  />
                }
                icon={t(title) === t(activeNavBtn) ? activeIcon ?? icon : icon}
                className="sidebar__item"
                active={t(title) === t(activeNavBtn)}
                collapsed={+isCollapsed}
                disabled={disabled}
              >
                {t(title)}
              </StyledSidebarMenuItem>
            </Tooltip>
          ))}
        </StyledSidebarMenu>
      </StyledSidebarContainer>
      {renderedFooter}
      {renderedVersion}
    </StyledSidebar>
  );
};

export default Sidebar;
